import { X } from "lucide-react"
import { Controller, type Control, type UseFormSetValue } from "react-hook-form"

import { LocationAutocompleteInput, type ResolvedLocationSelection } from "@/components/location-autocomplete-input"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  normalizeStructuredLocation,
  type FilterFormData,
  type FilterLocationFullItem,
} from "@/lib/search-filter-model"

interface LocationFiltersSectionProps {
  control: Control<FilterFormData>
  formValues: FilterFormData
  onRemoveLocation: (location: string) => void
  onRemoveRegion: (region: string) => void
  onRemoveStructuredLocation: (location: FilterLocationFullItem) => void
  onSelectLocation: (location: ResolvedLocationSelection) => void
  setValue: UseFormSetValue<FilterFormData>
}

export function LocationFiltersSection({
  control,
  formValues,
  onRemoveLocation,
  onRemoveRegion,
  onRemoveStructuredLocation,
  onSelectLocation,
  setValue,
}: LocationFiltersSectionProps) {
  const structuredLocationBadges = formValues.locationFull
    .map((location) => normalizeStructuredLocation(location))
    .filter((location) => location.city || location.region || location.country || location.label)

  const fallbackLocationBadges =
    structuredLocationBadges.length === 0
      ? [
          ...formValues.locations.map((location) => ({
            key: `city-${location}`,
            kind:
              formValues.locationCountry === "US"
                ? "City"
                : formValues.locationCountry === "CA"
                  ? "City"
                  : "Location",
            onRemove: () => onRemoveLocation(location),
            value: location,
          })),
          ...formValues.locationRegion.map((location) => ({
            key: `region-${location}`,
            kind: formValues.locationCountry === "US" ? "State" : "Province",
            onRemove: () => onRemoveRegion(location),
            value: location,
          })),
        ]
      : []

  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <Label className="text-teal-600">Location(s)</Label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Within 25 miles</span>
            <Button
              variant="link"
              size="sm"
              className="p-0 text-teal-600"
              onClick={() => {
                setValue("locations", [], { shouldDirty: true })
                setValue("locationRegion", [], { shouldDirty: true })
                setValue("locationCountry", "", { shouldDirty: true })
                setValue("locationInput", "", { shouldDirty: true })
                setValue("locationFull", [], { shouldDirty: true })
              }}
              type="button"
            >
              Clear all
            </Button>
          </div>
        </div>
        <Controller
          name="locationInput"
          control={control}
          render={({ field }) => (
            <LocationAutocompleteInput
              value={field.value ?? ""}
              onValueChange={field.onChange}
              onSelectLocation={onSelectLocation}
              countryHint={
                formValues.locationCountry === "US" || formValues.locationCountry === "CA"
                  ? formValues.locationCountry
                  : undefined
              }
            />
          )}
        />
        <div className="mt-2 flex flex-wrap gap-2">
          {structuredLocationBadges.map((location) => {
            const locationLabel =
              [location.city, location.region].filter(Boolean).join(", ") ||
              location.label ||
              location.region ||
              location.country ||
              ""
            const badgeLabel =
              location.city ? "City" : formValues.locationCountry === "US" ? "State" : "Province"

            return (
              <Badge
                key={`${location.city}-${location.region}-${location.country}-${location.label}`}
                variant="secondary"
                className="flex items-center gap-1"
              >
                <span className="rounded bg-teal-100 px-1 text-xs text-teal-800">{badgeLabel}</span>
                {locationLabel}
                <X className="h-3 w-3 cursor-pointer" onClick={() => onRemoveStructuredLocation(location)} />
              </Badge>
            )
          })}
          {fallbackLocationBadges.map((location) => (
            <Badge key={location.key} variant="secondary" className="flex items-center gap-1">
              <span className="rounded bg-teal-100 px-1 text-xs text-teal-800">{location.kind}</span>
              {location.value}
              <X className="h-3 w-3 cursor-pointer" onClick={location.onRemove} />
            </Badge>
          ))}
        </div>
      </div>
    </div>
  )
}
